// Issue severity levels
export type ReviewIssueSeverity = 'critical' | 'warning' | 'suggestion';

// Issue categories that map to actionable fixes
export type ReviewIssueType =
  | 'text_contrast'
  | 'text_overflow'
  | 'text_too_small'
  | 'text_too_large'
  | 'logo_size'
  | 'layout_crowded'
  | 'layout_empty'
  | 'color_clash'
  | 'cta_visibility'
  | 'composition'
  | 'alignment'
  | 'readability'
  | 'other';

// A proposed fix that maps to template element modifications
export interface ReviewProposedFix {
  elementId?: string;
  transform?: Partial<{
    positionX: number;
    positionY: number;
    width: number;
    height: number;
  }>;
  textColor?: string;
  fontSize?: number;
  outlineColor?: string;
  outlineWidth?: number;
  backgroundColor?: string;
  opacity?: number;
  confidence?: number; // 0-1, how confident the auto-fix is
  fontWeight?: string;
  fontFamily?: string;
  textAlign?: string;
  letterSpacing?: number;
  lineHeight?: number;
}

// A single detected issue
export interface ReviewIssue {
  type: ReviewIssueType;
  severity: ReviewIssueSeverity;
  description: string;
  suggestion: string;
  affectedElementIds?: string[];
  proposedFix?: ReviewProposedFix;
}

// Metadata sent alongside the image for context
export interface ReviewMetadata {
  templateId?: string;
  templateName?: string;
  layoutType?: string;
  canvasWidth: number;
  canvasHeight: number;
  elementCount: number;
  dynamicTextTypes: string[];
  imageSource: string;
  brandColors?: string[];
  brandFonts?: string[];
  industry?: string;
  brandName?: string;
}

// Complete review result
export interface AdReview {
  id: string;
  postId: string;
  workspaceId: string;
  score: number;
  issues: ReviewIssue[];
  metadata: ReviewMetadata;
  modelUsed: string;
  createdAt: string;
}

// Request to create a review
export interface CreateReviewRequest {
  imageBase64: string;
  metadata: ReviewMetadata;
}

// Response from the review endpoint
export interface ReviewResponse {
  review: AdReview;
  cached: boolean;
  reviewsRemaining: number;
}

// Review tier capabilities
export type ReviewTier = 'none' | 'review_only' | 'review_and_fix';

// Review capability for current user
export interface ReviewCapability {
  tier: ReviewTier;
  reviewLimit: number;
  reviewsUsed: number;
  reviewsRemaining: number;
}

// Review history entry (lightweight)
export interface ReviewHistoryEntry {
  id: string;
  score: number;
  issueCount: number;
  fixesApplied: number;
  previousScore: number | null;
  createdAt: string;
}
